import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaLoader } from '../../hooks/useMediaLoader';
import AnimatronicCardBase from './cards/AnimatronicCardBase';
import AnimatronicCardContent from './cards/AnimatronicCardContent';
import type { Animatronic } from '../../types/animatronic';
import { motion } from 'framer-motion';
import LoadingState from '../ui/LoadingState';
import ProgressiveImage from '../ui/ProgressiveImage';

interface AnimatronicCardProps {
  animatronic: Animatronic;
}

const AnimatronicCard: React.FC<AnimatronicCardProps> = ({ animatronic }) => {
  const navigate = useNavigate();
  const { validMedia, isLoading } = useMediaLoader(animatronic.media);

  const handleCardClick = (e: React.MouseEvent) => {
    if (!(e.target as HTMLElement).closest('.swiper-button-next, .swiper-button-prev')) {
      navigate(`/database/${animatronic.id}`);
    }
  };

  if (isLoading) {
    return <LoadingState type="skeleton" />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.3 }}
      className="touch-manipulation"
    >
      <div 
        className="bg-[#2A2A2A] rounded-xl overflow-hidden border border-gray-800 hover:border-brand/50 transition-all group hover:shadow-[0_0_15px_rgba(62,207,142,0.3)] duration-300 cursor-pointer active:scale-[0.98]"
        onClick={handleCardClick}
      >
        <div className="relative aspect-video">
          {validMedia[0] && (
            <ProgressiveImage
              src={validMedia[0].url}
              alt={animatronic.name}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute top-2 right-2 z-10">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand/20 text-brand backdrop-blur-sm">
              {animatronic.type}
            </span>
          </div>
        </div>
        
        <div className="p-5">
          <AnimatronicCardContent
            name={animatronic.name}
            attraction={animatronic.attraction}
            country={animatronic.country}
            year={animatronic.year}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default AnimatronicCard;